"use client";

import { Calendar, User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function formatDate(dateStr?: string) {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="h-6 w-32 bg-muted/40 rounded animate-pulse mb-6" />
        <div className="h-12 w-full bg-muted/40 rounded animate-pulse mb-4" />
        <div className="h-12 w-3/4 bg-muted/40 rounded animate-pulse mb-8" />
        <div className="aspect-[16/9] w-full bg-muted/40 rounded-lg animate-pulse mb-10" />
        <div className="space-y-3">
          <div className="h-4 w-full bg-muted/40 rounded animate-pulse" />
          <div className="h-4 w-full bg-muted/40 rounded animate-pulse" />
          <div className="h-4 w-2/3 bg-muted/40 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-20 text-center">
        <p className="text-muted-foreground">Failed to load article.</p>
      </div>
    );
  }

  const post = posts && posts.length > 0 ? posts[0] : null;

  if (!post) {
    return (
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-20 text-center">
        <p className="text-muted-foreground">Article not found.</p>
      </div>
    );
  }

  const authorName = post.author?.name || "";
  const authorAvatar =
    post.author?.avatar && (post.author.avatar["96"] || post.author.avatar["48"] || post.author.avatar["24"]);
  const authorInitial = authorName ? authorName.charAt(0).toUpperCase() : "M";

  return (
    <article className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-12 md:py-20">
      {}
      {post.categories && post.categories.length > 0 && (
        <div className="mb-6 flex flex-wrap gap-2">
          {post.categories.map((cat, i) => (
            <span
              data-index={i}
              key={cat.id}
              className="inline-flex items-center px-3 py-1 text-xs font-medium tracking-wide uppercase bg-primary text-white rounded-md"
            >
              {cat.name}
            </span>
          ))}
        </div>
      )}

      {}
      <h1
        className="font-default font-serif text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tight text-primary leading-tight mb-6"
        dangerouslySetInnerHTML={{ __html: post.title }}
      />

      {}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-slate-600 mb-10">
        {authorName && (
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-primary" />
            <span>{authorName}</span>
          </div>
        )}
        {post.date && (
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-primary" />
            <time dateTime={post.date}>{formatDate(post.date)}</time>
          </div>
        )}
      </div>

      <Separator className="mb-10 border-slate-200" />

      {}
      {post.featuredImage && (
        <div className="mb-12 overflow-hidden rounded-lg shadow-md border border-slate-200">
          <img
            src={post.featuredImage}
            alt={post.title}
            loading="lazy"
            decoding="async"
            className="w-full h-auto object-cover"
          />
        </div>
      )}

      {}
      {post.content && (
        <div
          className="font-default prose prose-lg max-w-none text-slate-800 leading-relaxed [&>p]:mb-6 [&>h2]:font-serif [&>h2]:text-3xl [&>h2]:font-semibold [&>h2]:mt-12 [&>h2]:mb-4 [&>h2]:text-primary [&>h3]:font-serif [&>h3]:text-2xl [&>h3]:font-semibold [&>h3]:mt-10 [&>h3]:mb-3 [&>h3]:text-primary [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-6 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-6 [&>blockquote]:border-l-4 [&>blockquote]:border-primary [&>blockquote]:pl-4 [&>blockquote]:italic [&>blockquote]:text-slate-600 [&>blockquote]:my-6 [&>a]:text-primary [&>a]:underline [&>img]:rounded-md [&>img]:my-6"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-12 flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-slate-500 mr-2">Topics:</span>
          {post.tags.map((tag, i) => (
            <span
              data-index={i}
              key={tag.id}
              className="inline-flex items-center px-3 py-1 text-xs bg-slate-100 text-primary rounded-md border border-slate-200"
            >
              #{tag.name}
            </span>
          ))}
        </div>
      )}

      <Separator className="my-12 border-slate-200" />

      {}
      {authorName && (
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="flex flex-col sm:flex-row gap-6 items-start sm:items-center">
            <Avatar className="h-20 w-20 rounded-lg">
              {authorAvatar && <AvatarImage src={authorAvatar} alt={authorName} />}
              <AvatarFallback className="rounded-lg bg-primary text-white font-default font-serif text-2xl">
                {authorInitial}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Maritime Contributor</p>
              <h3 className="font-default font-serif text-2xl font-semibold text-primary mb-2">
                {authorName}
              </h3>
              <p className="text-sm text-slate-600">
                Dedicated to preserving the heritage of the Miss Yvonne and sharing the bounty of our Gulf Coast waters.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {}
      <div className="mt-12 text-center">
        <Link to="/HomePage">
          <Button variant="outline" size="lg" className="border-primary text-primary hover:bg-primary hover:text-white">
            Return to Home
          </Button>
        </Link>
      </div>
    </article>
  );
}

export default function Post() {
  const currentWPQueryParams = (window as any).wvcClient.getCurrentWPQueryParams();
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="2136" id="single-post-template-section" className="bg-background text-foreground">
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}